import { Injectable } from '@angular/core';
import {
  IPropertiesDecrypt,
  IPropertiesEncrypt,
  NaclSbxUtil,
} from '@npm-bbta/bbog-ate-sbx-utilities-lib';
import { HttpHeaders } from '@angular/common/http';
import { MD5 } from 'crypto-js';
import { environment } from '../../../../environments/environment';
import {HeadersRequestEnum} from '../../enums/headers-request.enum';
import {BdbSessionStrgService} from '@npm-bbta/bbog-cm-auth-lib';
import {SessionStorageKeysEnum} from '../../models/session-storage-keys-enum';
import {IAxiosResponse} from '../../models/IAxiosResponse';


@Injectable()
export class SandboxUtilsService {
  constructor(private readonly sessionStorageService: BdbSessionStrgService) {}

  /**
   * Method for encipher data
   * @param {any} data Data for encipher
   * @param {string} uuid Trace for sandbox
   */
  async encipherData(data: any, uuid: string) {
    const pk = environment.sandbox.pk;
    const sandboxParameters = await this.getParametersEncryptForSandbox(uuid);
    try {
      return NaclSbxUtil.generateJWTPublicKey(
        data,
        pk,
        sandboxParameters
      );
    } catch (e) {
      console.log('[ERROR] Error in encipherData', e);
      return data;
    }
  }

  /**
   * Method for decipher data
   * @param {any} data Data for encipher
   * @param {any} authToken Data for encipher
   */
  async decipherData(data: string, authToken: string) {
    const sk = environment.sandbox.sk;
    const sandboxParameters = this.getParametersDecryptForSandbox(authToken);
    try {
      return NaclSbxUtil.validateJWT(data, sk, sandboxParameters);
    } catch (e) {
      console.log('[ERROR] Error in decipherData', e);
      return data;
    }
  }

  /**
   * Method for update headers required in sandbox
   * @param {any} headersRequest - The headers of the request to be updated with sandbox headers.
   * @param {string} encryptedBody - Encrypted body to be used to generate X-strcode header.
   * @param {string} uuid - Uuid of the request.
   * @param {boolean?} shouldSetStrCode - Optional. When it is sent, X-strcode is not set as a header. Hence, request is sent through
   * sandbox but the response does not come ciphered.
   * @return {any} updateHeaders
   */


    async updateHeaders(headersRequest: any, encryptedBody: string, uuid: string) {
        const header = {};
        header[`${HeadersRequestEnum.X_CUSTOM_HEADERS}`] = headersRequest ? await this.generateCustomHeaders(headersRequest, uuid) : headersRequest;
        header[`${HeadersRequestEnum.CONTENT_TYPE}`] = 'text/plain';
        header[`${HeadersRequestEnum.X_STRCODE}`] = this.generateHashMd5(encryptedBody);
        header[`${HeadersRequestEnum.X_AUTH_TOKEN}`] = 'v3';
        header[`${HeadersRequestEnum.X_CHANNEL}`] = this.sessionStorageService.get(SessionStorageKeysEnum.CHANNEL) || 'Oficina';
        header[`${HeadersRequestEnum.X_RQ_UID}`] = uuid;
        header[`${HeadersRequestEnum.X_UUIDAUTH}`] = uuid;
        return header;
    }


  /**
   * Method for update sandbox headers with the headers required in the resource to be consumed.
   * @param {HttpHeaders} customHeaders - The headers already set for sandbox request.
   * @param {key: string, value: string} initialHeaders - The headers of the resource request.
   * @return {HttpHeaders} Headers of sandbox and resource set.
   */
  updateWithInitialHeaders(
    customHeaders: HttpHeaders,
    initialHeaders: Record<string, string>
  ): HttpHeaders {
    for (const [header, value] of Object.entries(initialHeaders)) {
      customHeaders = customHeaders.set(header, value);
    }
    return customHeaders;
  }

    private async generateCustomHeaders(
        headersRequest: any,
        uuid: string
    ): Promise<string> {
        const headersResp: any[] = [];
        const headersDef: string[] = Object.keys(headersRequest);
        headersDef.forEach(name => {
            if (name && headersRequest[name]) {
                headersResp.push({[name]: headersRequest[name]});
            }
        });
        return this.encipherData(headersResp, uuid);
    }

  /**
   * Method for obtain parameters required in sandbox encrypt
   * @param {string} uuid
   * @return {IPropertiesEncrypt} properties
   * @private
   */
  async getParametersEncryptForSandbox(
    uuid: string
  ): Promise<IPropertiesEncrypt> {
    const propertiesSandbox =
      environment.propertiesSandbox as IPropertiesEncrypt;
    propertiesSandbox.Seed = {
      nonce: NaclSbxUtil.generateCustomNonce(uuid),
    };
    return propertiesSandbox;
  }

  /**
   * Method for obtain parameters required in sandbox decrypt
   * @return {IPropertiesDecrypt} properties
   * @private
   */
  getParametersDecryptForSandbox(authToken: string): IPropertiesDecrypt {
    const parameters: any = environment.propertiesSandbox;
    parameters.Seed = {
      nonce: NaclSbxUtil.generateCustomNonce(authToken),
    };
    return parameters;
  }

  /**
   * Method for generate hash in md5 for request
   * @param {any} value
   * @return {string} valueInHash
   * @private
   */
  generateHashMd5(value: any): string {
    return MD5(value).toString();
  }

  getHeadersResponse(response: IAxiosResponse) {
    let headersResponse: Record<string, string> = {};

    try{
      response.headers.keys().forEach(key => {
        headersResponse = {
          ...headersResponse,
          [`${key}`]: response.headers.get(key),
        };
      });
    } catch {
      headersResponse = {
        ...headersResponse,
        ['x-auth-token']: this.sessionStorageService.get('uuidAuth'),
      };
    }

    return headersResponse;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/sandbox/sandbox-utils.service.ts